/* Admin console data layer: the authenticated API client, a small cached
   query hook shared by every section, and the formatters and user helpers the
   panels have in common. */
import { useCallback, useEffect, useReducer, useRef } from 'react';
import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
export const API = `${BACKEND_URL}/api`;

async function authConfig() {
  try {
    const { supabase } = await import('@/lib/supabase');
    const { data: { session } } = await supabase.auth.getSession();
    if (session?.access_token) return { headers: { Authorization: `Bearer ${session.access_token}` } };
  } catch { /* fall back to the cookie session */ }
  return { withCredentials: true };
}

/* Every call resolves to the response body. */
export const api = {
  get: async (path) => (await axios.get(`${API}${path}`, await authConfig())).data,
  post: async (path, body = {}) => (await axios.post(`${API}${path}`, body, await authConfig())).data,
  patch: async (path, body = {}) => (await axios.patch(`${API}${path}`, body, await authConfig())).data,
  put: async (path, body = {}) => (await axios.put(`${API}${path}`, body, await authConfig())).data,
  del: async (path) => (await axios.delete(`${API}${path}`, await authConfig())).data,
};

export function errorMessage(err, fallback = 'Something went wrong') {
  const status = err?.response?.status;
  if (status === 401) return 'Your session has expired. Sign in again.';
  if (status === 403) return 'This account does not have admin access.';
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (Array.isArray(detail)) return detail.map((d) => d?.msg).filter(Boolean).join('; ') || fallback;
  if (err?.message === 'Network Error') return 'Could not reach the server.';
  return err?.message || fallback;
}

/* ── Cached queries ──────────────────────────────────────────────────────────
   One cache for the whole console, so switching sections shows the last data
   at once instead of a spinner, and sections that share a list (customers,
   tickets) share a single request. */
const cache = new Map();        // key → { data, error, updatedAt, promise }
const subscribers = new Map();  // key → Set of re-render callbacks

const entryOf = (key) => cache.get(key) || {};
const emit = (key) => subscribers.get(key)?.forEach((fn) => fn());

export function loadQuery(key, fetcher, { force = false, maxAge = 60_000 } = {}) {
  const entry = entryOf(key);
  if (entry.promise) return entry.promise;
  if (!force && entry.updatedAt && !entry.error && Date.now() - entry.updatedAt < maxAge) {
    return Promise.resolve(entry.data);
  }
  const promise = fetcher().then(
    (data) => {
      cache.set(key, { data, error: null, updatedAt: Date.now(), promise: null });
      emit(key);
      return data;
    },
    (error) => {
      cache.set(key, { ...entryOf(key), error, promise: null });
      emit(key);
      throw error;
    },
  );
  cache.set(key, { ...entry, promise });
  emit(key);
  return promise;
}

/* Mark every cached query whose key starts with `prefix` as stale; the next
   component that reads it refetches. */
export function invalidateQueries(prefix) {
  cache.forEach((entry, key) => {
    if (key.startsWith(prefix)) cache.set(key, { ...entry, updatedAt: 0 });
  });
}

export function setQueryData(key, updater) {
  const entry = entryOf(key);
  const data = typeof updater === 'function' ? updater(entry.data) : updater;
  cache.set(key, { ...entry, data });
  emit(key);
}

export function useQuery(key, fetcher, { enabled = true, maxAge } = {}) {
  const [, rerender] = useReducer((n) => n + 1, 0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  useEffect(() => {
    if (!key) return undefined;
    if (!subscribers.has(key)) subscribers.set(key, new Set());
    const set = subscribers.get(key);
    set.add(rerender);
    return () => { set.delete(rerender); };
  }, [key]);

  useEffect(() => {
    if (key && enabled) loadQuery(key, () => fetcherRef.current(), { maxAge }).catch(() => {});
  }, [key, enabled, maxAge]);

  const refetch = useCallback(
    () => (key ? loadQuery(key, () => fetcherRef.current(), { force: true }).catch(() => undefined) : Promise.resolve()),
    [key],
  );
  const setData = useCallback((updater) => key && setQueryData(key, updater), [key]);

  const entry = key ? entryOf(key) : {};
  return {
    data: entry.data,
    error: entry.error || null,
    loading: !!entry.promise,
    updatedAt: entry.updatedAt || null,
    refetch,
    setData,
  };
}

/* Shared lists. */
export const useUsers = () => useQuery('users', async () => (await api.get('/admin/users')).users || []);
export const useTickets = () => useQuery('tickets', async () => {
  const data = await api.get('/support?limit=1000');
  return Array.isArray(data) ? data : [];
});
export const useConfigFlag = (key) => useQuery(`config:${key}`, async () => {
  try {
    return (await api.get(`/config/${key}`)).value === true;
  } catch (err) {
    if (err?.response?.status === 404) return false; // never set → off
    throw err;
  }
});

/* ── Formatting ──────────────────────────────────────────────────────────── */
const isNum = (n) => n !== null && n !== undefined && n !== '' && Number.isFinite(Number(n));

export const fmtInt = (n) => (isNum(n) ? new Intl.NumberFormat('en-US').format(Math.round(Number(n))) : '—');
export const fmtPct = (n, digits = 1) => (isNum(n) ? `${Number(n).toFixed(digits)}%` : '—');
export const pctOf = (part, whole) => (whole ? (part / whole) * 100 : null);

export function fmtMoney(amount, currency = 'EUR') {
  if (!isNum(amount)) return '—';
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2 })
      .format(Number(amount));
  } catch {
    return `${Number(amount).toFixed(2)} ${currency}`;
  }
}

/* Date-only strings ("2026-09-30") are calendar days, not UTC instants. */
export function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number);
    return new Date(y, m - 1, d);
  }
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

export const fmtDate = (value) => {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
};
export const fmtDay = (value) => {
  const d = parseDate(value);
  return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
};
export const fmtDateTime = (value) => {
  const d = parseDate(value);
  return d
    ? d.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '—';
};

export function timeAgo(value) {
  const d = parseDate(value);
  if (!d) return '—';
  const s = Math.round((Date.now() - d.getTime()) / 1000);
  if (s < 45) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  if (s < 86400 * 30) return `${Math.round(s / 86400)} d ago`;
  return fmtDate(d);
}

/* Local calendar day as YYYY-MM-DD. */
export const isoDay = (d) => {
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export function lastNDays(n) {
  const out = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(d.getDate() - i);
    out.push(isoDay(d));
  }
  return out;
}

export const humanize = (value) => {
  if (value === undefined || value === null || value === '') return null;
  return String(value).replace(/_/g, ' ').replace(/^\w/, (c) => c.toUpperCase());
};

/* Multi-select answers arrive as arrays, JSON strings or comma lists. */
export function toList(value) {
  if (Array.isArray(value)) return value.filter(Boolean);
  if (!value) return [];
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch { /* not JSON */ }
    return value.split(',').map((s) => s.trim()).filter(Boolean);
  }
  return [];
}

export function downloadCsv(filename, headers, rows) {
  const esc = (c) => `"${String(c ?? '').replace(/"/g, '""')}"`;
  const csv = [headers, ...rows].map((r) => r.map(esc).join(',')).join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

/* ── Users ───────────────────────────────────────────────────────────────── */
/* Admins and betatesters are staff: comped premium, excluded from business
   numbers so internal logins never skew them. */
export const STAFF_ROLES = new Set(['admin', 'betatester']);
export const isStaff = (u) => STAFF_ROLES.has(u?.role);
export const planOf = (u) => u?.subscription_status || 'free';
export const displayName = (u) => u?.name || (u?.email ? u.email.split('@')[0] : 'Unnamed user');
export const initials = (u) => (u?.name || u?.email || '?').trim().charAt(0).toUpperCase();

export const PLAN_LABEL = { premium: 'Premium', trial: 'Trial', expired: 'Expired', free: 'Free' };
export const PLAN_TONE = { premium: 'success', trial: 'info', expired: 'danger', free: 'neutral' };
export const ROLE_LABEL = { admin: 'Admin', betatester: 'Beta tester', user: 'User' };

/* kids_cluster is the onboarding answer ("Do you have kids?" + their ages:
   0–5 or 6–12 → young, 13+ → grown, several groups → mixed). older and teens
   come from earlier onboardings. */
export const KIDS_LABEL = {
  none: 'No kids',
  young: 'Kids under 13',
  grown: 'Teens or adult kids',
  mixed: 'Kids of different ages',
  older: 'Older kids',
  teens: 'Teens',
};
export const hasKids = (u) => (!!u?.kids_cluster && u.kids_cluster !== 'none') || Number(u?.number_of_children) > 0;

export const countryFlag = (code) => {
  if (!code || String(code).length !== 2) return '';
  return String.fromCodePoint(...[...String(code).toUpperCase()].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
};
